import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getAttribution, readUrlUtm } from '../utils/utm';

const API = process.env.REACT_APP_BACKEND_URL;
const CLIENT_ID_KEY = 'outlaw_cid';

const AuthContext = createContext(null);

const getClientId = () => {
  try {
    // The server issues `outlaw_cid` as a long-lived HTTP cookie - that is the
    // source of truth. Safari wipes localStorage and JS-written cookies after
    // ~7 days of no visits, which used to make one phone look like a new
    // device every week, so we only fall back to localStorage when the cookie
    // hasn't been issued yet (first ever request).
    const fromCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${CLIENT_ID_KEY}=`))
      ?.split('=')[1];
    let cid = fromCookie || localStorage.getItem(CLIENT_ID_KEY);
    if (!cid) {
      cid = (window.crypto?.randomUUID?.() || `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    }
    // Keep a local mirror so a cookie-less first hit can still adopt this id.
    localStorage.setItem(CLIENT_ID_KEY, cid);
    return cid;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [meter, setMeter] = useState(null);
  const [grace, setGrace] = useState(null);
  const [loading, setLoading] = useState(true);
  const clientId = useMemo(() => getClientId(), []);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/me?client_id=${encodeURIComponent(clientId || '')}`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('bad');
      const data = await res.json();
      setUser(data.user || null);
      setMeter(data.meter || null);
      setGrace(data.grace || null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    // If we're mid-Google-callback the hash contains session_id=...; skip
    // the /api/me probe and let AuthGoogleCallback set the cookie first,
    // otherwise we race and this call 401s.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const requestMagicLink = useCallback(async (email) => {
    // Send the stored campaign attribution with the request so the signup is
    // attributed even if the link is opened on another device.
    const { last_touch: lastTouch, first_touch: firstTouch } = getAttribution();
    // Backup for blocked storage: tags sitting in the current URL.
    const urlUtm = readUrlUtm();
    const last = lastTouch || (urlUtm ? { ...urlUtm, landing_page: `${window.location.pathname}${window.location.search}` } : null);
    const res = await fetch(`${API}/api/auth/magic-link`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        ...(last ? { utm: last } : {}),
        ...(firstTouch ? { utm_first: firstTouch } : {}),
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Could not send login link.' }));
      throw new Error(err.detail || 'Could not send login link.');
    }
    return res.json();
  }, []);

  const joinOpenAccess = useCallback(async (email) => {
    // Open-access window: email in, straight through. Joins the newsletter and
    // creates a free membership account, signed in immediately.
    const { last_touch: lastTouch, first_touch: firstTouch } = getAttribution();
    const urlUtm = readUrlUtm();
    const last = lastTouch || (urlUtm ? { ...urlUtm, landing_page: `${window.location.pathname}${window.location.search}` } : null);
    const res = await fetch(`${API}/api/auth/open-access`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        ...(last ? { utm: last } : {}),
        ...(firstTouch ? { utm_first: firstTouch } : {}),
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Could not unlock. Try again.' }));
      throw new Error(err.detail || 'Could not unlock. Try again.');
    }
    const data = await res.json();
    await refresh();
    return data;
  }, [refresh]);

  const verifyMagicToken = useCallback(async (token) => {
    const res = await fetch(`${API}/api/auth/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, client_id: clientId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Invalid or expired link.' }));
      throw new Error(err.detail || 'Invalid or expired link.');
    }
    const data = await res.json();
    setUser(data.user);
    // Plausible goal: a reader completed signup / login via the magic link.
    try {
      if (window.plausible) {
        window.plausible('Signup', { props: { plan: data.user?.plan || 'free' } });
      }
    } catch { /* analytics must never break login */ }
    await refresh();
    return data.user;
  }, [clientId, refresh]);

  const logout = useCallback(async () => {
    await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    setUser(null);
    await refresh();
  }, [refresh]);

  // During an open-access window every signed-in reader gets full access -
  // the paywall re-arms by itself when the window closes.
  const hasAccess = !!user && (
    !!grace?.active ||
    (['monthly', 'annual'].includes(user.plan) && user.plan_status === 'active') ||
    (user.plan === 'founding' && user.plan_status === 'lifetime')
  );

  const value = useMemo(() => ({
    user, meter, grace, loading, clientId, hasAccess,
    requestMagicLink, verifyMagicToken, joinOpenAccess, logout, refresh,
  }), [user, meter, grace, loading, clientId, hasAccess, requestMagicLink, verifyMagicToken, joinOpenAccess, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
